import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  CardHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  Modal,
  ModalHeader,
  Form,
  ModalBody,
  Label,
  Input,
  FormFeedback,
} from "reactstrap";
import * as moment from "moment";
import { Link } from "react-router-dom";
import classnames from "classnames";
import Flatpickr from "react-flatpickr";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import TableContainer from "../../../Components/Common/TableContainer";
import DeleteModal from "../../../Components/Common/DeleteModal";
import { isEmpty } from "lodash";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useSelector, useDispatch } from "react-redux";

import {
  getSettlementPrices as onGetSettlementPrices,
  getPayments as onGetPayments,
  addNewPayment as onAddNewPayment,
  updatePayment as onUpdatePayment,
  deletePayment as onDeletePayment,
} from "../../../slices/thunks";

import Loader from "../../../Components/Common/Loader";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import ExportCSVModal from "../../../Components/Common/ExportCSVModal";
import { createSelector } from "reselect";
import { useProfile } from "../../../Components/Hooks/UserHooks";

const EcommerceOrders = () => {
  const [modal, setModal] = useState(false);
  const [activeTab, setActiveTab] = useState("1");
  const [settlementPrice, setSettlementPrice] = useState([]);

  const dispatch = useDispatch();

  const selectLayoutState = (state) => state.Payment;
  const selectLayoutProperties = createSelector(
    selectLayoutState,
    (payment) => ({
      payments: payment.payments,
      isPaymentSuccess: payment.isPaymentSuccess,
      error: payment.error,
    })
  );

  const { payments, isPaymentSuccess, error } = useSelector(
    selectLayoutProperties
  );
  const { userProfile } = useProfile();

  const isAdmin =
    userProfile?.user?.groups?.includes("Admin") ||
    userProfile?.user?.groups?.includes("Editor");
  useEffect(() => {
    dispatch(onGetSettlementPrices()).then((response) => {
      setSettlementPrice(response.payload || []);
    });
  }, [dispatch]);

  const [paymentList, setPaymentList] = useState([]);
  const [payment, setPayment] = useState([]);
  const [isEdit, setIsEdit] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteModalMulti, setDeleteModalMulti] = useState(false);

  const onClickDelete = (payment) => {
    setPayment(payment);
    setDeleteModal(true);
  };

  const handleDeletePayment = () => {
    if (payment) {
      dispatch(onDeletePayment(payment.id));
      setDeleteModal(false);
    }
  };

  useEffect(() => {
    setPaymentList(payments);
  }, [payments]);

  useEffect(() => {
    if (!isEmpty(payments)) setPaymentList(payments);
  }, [payments]);

  const toggleTab = (tab, type) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      let filteredPayments = payments;
      if (type !== "all") {
        filteredPayments = payments.filter(
          (payment) => payment.status === type
        );
      }
      setPaymentList(filteredPayments);
    }
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: (payment && payment.id) || "",
      type: (payment && payment.type) || "",
      tax: (payment && payment.tax) || "",
      vat_percentage: (payment && payment.vat_percentage) || 21,
      amount: (payment && payment.amount) || "",
      cashed: (payment && payment.cashed) || "",
      booking_date: (payment && payment.booking_date) || "",
      cashed_date: (payment && payment.cashed_date) || "",
      settlement_price: (payment && payment.settlement_price) || "",
    },
    validationSchema: Yup.object({
      type: Yup.string().required("Please Enter Type"),
      tax: Yup.number().required("Please Enter Tax"),
      vat_percentage: Yup.number().required("Please Enter VAT Percentage"),
      amount: Yup.number().required("Please Enter Amount"),
      cashed: Yup.number()
        .required("Please Enter Cashed Amount")
        .test(
          "cashed-not-exceed",
          "Cashed amount cannot exceed total amount.",
          function (value) {
            const { amount } = this.parent;
            return value <= amount;
          }
        ),
      booking_date: Yup.date().required("Please Enter Booking Date"),
      settlement_price: Yup.date().required("Please Enter Settlement Price"),
    }),
    onSubmit: (values) => {
      const sanitizeValues = (values) => {
        const sanitized = { ...values };
        for (let key in sanitized) {
          if (sanitized[key] === "") {
            sanitized[key] = null;
          }
        }
        if (sanitized.active === "" || sanitized.active === null) {
          sanitized.active = true;
        }
        return sanitized;
      };

      const sanitizedValues = sanitizeValues(values);

      if (isEdit) {
        const updatePayment = {
          id: payment ? payment.id : 0,
          type: sanitizedValues.type,
          tax: sanitizedValues.tax,
          vat_percentage: sanitizedValues.vat_percentage,
          amount: sanitizedValues.amount,
          cashed: sanitizedValues.cashed,
          booking_date: sanitizedValues.booking_date,
          cashed_date: sanitizedValues.cashed_date,
          settlement_price: sanitizedValues.settlement_price,
        };
        dispatch(onUpdatePayment(updatePayment));
        validation.resetForm();
      } else {
        const newPayment = {
          type: sanitizedValues.type,
          tax: sanitizedValues.tax,
          vat_percentage: sanitizedValues.vat_percentage,
          amount: sanitizedValues.amount,
          cashed: sanitizedValues.cashed,
          booking_date: sanitizedValues.booking_date,
          cashed_date: sanitizedValues.cashed_date,
          settlement_price: sanitizedValues.settlement_price,
        };
        dispatch(onAddNewPayment(newPayment));
        validation.resetForm();
      }
      toggle();
    },
  });

  useEffect(() => {
    dispatch(onGetPayments());
  }, [dispatch]);

  useEffect(() => {
    setPayment(payments);
  }, [payments]);

  useEffect(() => {
    if (!isEmpty(payments)) {
      setPayment(payments);
      setIsEdit(false);
    }
  }, [payments]);

  const toggle = useCallback(() => {
    if (modal) {
      setModal(false);
      setPayment(null);
    } else {
      setModal(true);
    }
  }, [modal]);

  const handlePaymentClicks = () => {
    setPayment("");
    setIsEdit(false);
    toggle();
  };

  const handlePaymentClick = useCallback(
    (arg) => {
      const payment = arg;

      const associatedSettelementPrice = settlementPrice.find(
        (set) => set.id === payment.settlement_price
      );
      if (!associatedSettelementPrice) {
        toast.error(
          "SettelementPrice data is not loaded yet. Please try again later."
        );
        return;
      }

      setPayment({
        id: payment.id,
        type: payment.type,
        tax: payment.tax,
        vat_percentage: payment.vat_percentage,
        amount: payment.amount,
        cashed: payment.cashed,
        booking_date: payment.booking_date,
        cashed_date: payment.cashed_date,
        settlement_price: payment.settlement_price,
      });

      setIsEdit(true);
      toggle();
    },
    [toggle, settlementPrice]
  );

  const checkedAll = useCallback(() => {
    const checkall = document.getElementById("checkBoxAll");
    const ele = document.querySelectorAll(".orderCheckBox");
    if (checkall.checked) {
      ele.forEach((ele) => {
        ele.checked = true;
      });
    } else {
      ele.forEach((ele) => {
        ele.checked = false;
      });
    }
    deleteCheckbox();
  }, []);

  const [selectedCheckBoxDelete, setSelectedCheckBoxDelete] = useState([]);
  const [isMultiDeleteButton, setIsMultiDeleteButton] = useState(false);

  const deleteMultiple = () => {
    const checkall = document.getElementById("checkBoxAll");
    selectedCheckBoxDelete.forEach((element) => {
      dispatch(onDeleteBlock(element.value));
      setTimeout(() => {
        toast.clearWaitingQueue();
      }, 3000);
    });
    setIsMultiDeleteButton(false);
    checkall.checked = false;
  };

  const deleteCheckbox = () => {
    const ele = document.querySelectorAll(".orderCheckBox:checked");
    ele.length > 0
      ? setIsMultiDeleteButton(true)
      : setIsMultiDeleteButton(false);
    setSelectedCheckBoxDelete(ele);
  };

  const columns = useMemo(
    () => [
      {
        header: "Type",
        accessorKey: "type",
        enableColumnFilter: false,
        cell: (cell) => <span>{cell.getValue()}</span>,
      },
      {
        header: "Tax",
        accessorKey: "tax",
        enableColumnFilter: false,
        cell: (cell) => <span>{cell.getValue()}</span>,
      },
      {
        header: "VAT (%)",
        accessorKey: "vat_percentage",
        enableColumnFilter: false,
        cell: (cell) => <span>{cell.getValue()}</span>,
      },
      {
        header: "Amount",
        accessorKey: "amount",
        enableColumnFilter: false,
        cell: (cell) => <span>{cell.getValue()}</span>,
      },
      {
        header: "Cashed",
        accessorKey: "cashed",
        enableColumnFilter: false,
        cell: (cell) => <span>{cell.getValue()}</span>,
      },
      {
        header: "Booking Date",
        accessorKey: "booking_date",
        enableColumnFilter: false,
        cell: (cell) => {
          const value = cell.getValue();
          if (value === null || value === undefined) {
            return <span style={{ color: "red" }}>No data</span>;
          }
          return (
            <>
              {handleValidDate(value)},
              <small className="text-muted"> {handleValidTime(value)}</small>
            </>
          );
        },
      },
      {
        header: "Cashed Date",
        accessorKey: "cashed_date",
        enableColumnFilter: false,
        cell: (cell) => {
          const value = cell.getValue();
          if (value === null || value === undefined) {
            return <span style={{ color: "red" }}>No data</span>;
          }
          return (
            <>
              {handleValidDate(value)},
              <small className="text-muted"> {handleValidTime(value)}</small>
            </>
          );
        },
      },
      {
        header: "Settelement Price",
        accessorKey: "settlement_price",
        enableColumnFilter: false,
        cell: (cell) => {
          const settelementId = cell.getValue();
          const settelement = settlementPrice.find(
            (s) => s.id === settelementId
          );
          return settelement ? (
            settelement.id
          ) : (
            <span style={{ color: "red" }}>Settelement not found</span>
          );
        },
      },
      {
        header: "Created At",
        accessorKey: "created_at",
        enableColumnFilter: false,
        cell: (cell) => (
          <>
            {handleValidDate(cell.getValue())},
            <small className="text-muted">
              {" "}
              {handleValidTime(cell.getValue())}
            </small>
          </>
        ),
      },
      {
        header: "Action",
        cell: (cellProps) => {
          const isAdmin =
            userProfile?.user?.groups?.includes("Admin") ||
            userProfile?.user?.groups?.includes("Editor");

          return (
            <ul className="list-inline hstack gap-2 mb-0">
              <li className="list-inline-item">
                <Link
                  to={`/payment-details/${cellProps.row.original.id}`}
                  className="text-primary d-inline-block"
                >
                  <i className="ri-eye-fill fs-16"></i>
                </Link>
              </li>
              {isAdmin && (
                <>
                  <li className="list-inline-item edit">
                    <Link
                      to="#"
                      className="text-primary d-inline-block edit-item-btn"
                      onClick={() => {
                        const paymentData = cellProps.row.original;
                        handlePaymentClick(paymentData);
                      }}
                    >
                      <i className="ri-pencil-fill fs-16"></i>
                    </Link>
                  </li>
                  <li className="list-inline-item">
                    <Link
                      to="#"
                      className="text-danger d-inline-block remove-item-btn"
                      onClick={() => {
                        const paymentData = cellProps.row.original;
                        onClickDelete(paymentData);
                      }}
                    >
                      <i className="ri-delete-bin-5-fill fs-16"></i>
                    </Link>
                  </li>
                </>
              )}
            </ul>
          );
        },
      },
    ],
    [handlePaymentClick, checkedAll]
  );

  const defaultdate = () => {
    let d = new Date(),
      months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
    let h = d.getHours() % 12 || 12;
    let ampm = d.getHours() < 12 ? "AM" : "PM";
    return (
      d.getDate() +
      " " +
      months[d.getMonth()] +
      ", " +
      d.getFullYear() +
      ", " +
      h +
      ":" +
      d.getMinutes() +
      " " +
      ampm
    ).toString();
  };

  const handleValidDate = (date) => {
    const date1 = moment(new Date(date)).format("DD MMM Y");
    return date1;
  };

  const handleValidTime = (time) => {
    const time1 = new Date(time);
    const getHour = time1.getUTCHours();
    const getMin = time1.getUTCMinutes();
    const getTime = `${getHour}:${getMin}`;
    var meridiem = "";
    if (getHour >= 12) {
      meridiem = "PM";
    } else {
      meridiem = "AM";
    }
    const updateTime =
      moment(getTime, "hh:mm").format("hh:mm") + " " + meridiem;
    return updateTime;
  };

  const [isExportCSV, setIsExportCSV] = useState(false);

  return (
    <div className="page-content">
      <ExportCSVModal
        show={isExportCSV}
        onCloseClick={() => setIsExportCSV(false)}
        data={paymentList}
      />

      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeletePayment}
        onCloseClick={() => setDeleteModal(false)}
      />
      <DeleteModal
        show={deleteModalMulti}
        onDeleteClick={() => {
          deleteMultiple();
          setDeleteModalMulti(false);
        }}
        onCloseClick={() => setDeleteModalMulti(false)}
      />
      <Container fluid>
        <BreadCrumb title="Payment" pageTitle="Tables" />
        <Row>
          <Col lg={12}>
            <Card id="orderList">
              <CardHeader className="border-0">
                <Row className="align-items-center gy-3">
                  <div className="col-sm">
                    <h5 className="card-title mb-0">Payments</h5>
                  </div>
                  <div className="col-sm-auto">
                    <div className="d-flex gap-1 flex-wrap">
                      {isAdmin && (
                        <button
                          type="button"
                          className="btn btn-success add-btn"
                          id="create-btn"
                          onClick={() => {
                            setIsEdit(false);
                            toggle();
                          }}
                        >
                          <i className="ri-add-line align-bottom me-1"></i>{" "}
                          Create Payment
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn-info"
                        onClick={() => setIsExportCSV(true)}
                      >
                        <i className="ri-file-download-line align-bottom me-1"></i>{" "}
                        Export
                      </button>{" "}
                      {isMultiDeleteButton && (
                        <button
                          className="btn btn-soft-danger"
                          onClick={() => setDeleteModalMulti(true)}
                        >
                          <i className="ri-delete-bin-2-line"></i>
                        </button>
                      )}
                    </div>
                  </div>
                </Row>
              </CardHeader>

              <CardBody className="pt-0">
                <div>
                  <Nav
                    className="nav-tabs nav-tabs-custom nav-success"
                    role="tablist"
                  >
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === "1" })}
                        onClick={() => {
                          toggleTab("1", "all");
                        }}
                        href="#"
                      >
                        <i className="ri-store-2-fill me-1 align-bottom"></i>{" "}
                        All Payments
                      </NavLink>
                    </NavItem>
                  </Nav>
                  {isPaymentSuccess ? (
                    settlementPrice.length === 0 ? (
                      <div className="text-center mt-5">
                        <p className="text-danger">
                          No settlement prices are available. Please add a
                          settlement price before managing payments.
                        </p>
                      </div>
                    ) : paymentList.length > 0 ? (
                      <TableContainer
                        columns={columns}
                        data={paymentList || []}
                        isGlobalFilter={true}
                        isAddUserList={false}
                        customPageSize={20}
                        divClass="table-responsive table-card mb-1"
                        tableClass="align-middle table-nowrap"
                        theadClass="table-light text-muted text-uppercase"
                        handlePaymentClick={handlePaymentClick}
                        // isOrderFilter={true}
                        SearchPlaceholder="Search for order ID, customer, order status or something..."
                      />
                    ) : (
                      <div className="text-center mt-5">
                        <p className="text-muted">
                          No payments are introduced yet. Please add a new
                          payment.
                        </p>
                      </div>
                    )
                  ) : (
                    <Loader error={error} />
                  )}
                </div>
                <Modal id="showModal" isOpen={modal} toggle={toggle} centered>
                  <ModalHeader className="bg-light p-3" toggle={toggle}>
                    {!!isEdit ? "Edit Payment" : "Add Payment"}
                  </ModalHeader>

                  <Form
                    className="tablelist-form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      validation.handleSubmit();
                      return false;
                    }}
                  >
                    <ModalBody>
                      <input type="hidden" id="id-field" />

                      {/* Type Field */}
                      <div className="mb-3">
                        <Label htmlFor="type-field" className="form-label">
                          Type
                        </Label>
                        <Input
                          name="type"
                          id="type-field"
                          className="form-control"
                          placeholder="Enter Payment Type"
                          type="text"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.type || ""}
                          invalid={
                            validation.touched.type && validation.errors.type
                              ? true
                              : false
                          }
                        />
                        {validation.touched.type && validation.errors.type ? (
                          <FormFeedback type="invalid">
                            {validation.errors.type}
                          </FormFeedback>
                        ) : null}
                      </div>

                      {/* Tax Field */}
                      <div className="mb-3">
                        <Label htmlFor="tax-field" className="form-label">
                          Tax
                        </Label>
                        <Input
                          name="tax"
                          id="tax-field"
                          className="form-control"
                          placeholder="Enter Tax Amount"
                          type="number"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.tax || ""}
                          invalid={
                            validation.touched.tax && validation.errors.tax
                              ? true
                              : false
                          }
                        />
                        {validation.touched.tax && validation.errors.tax ? (
                          <FormFeedback type="invalid">
                            {validation.errors.tax}
                          </FormFeedback>
                        ) : null}
                      </div>

                      {/* VAT Percentage Field */}
                      <div className="mb-3">
                        <Label
                          htmlFor="vat_percentage-field"
                          className="form-label"
                        >
                          VAT Percentage
                        </Label>
                        <Input
                          name="vat_percentage"
                          id="vat_percentage-field"
                          className="form-control"
                          placeholder="Enter VAT Percentage"
                          type="number"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.vat_percentage || ""}
                          invalid={
                            validation.touched.vat_percentage &&
                            validation.errors.vat_percentage
                              ? true
                              : false
                          }
                        />
                        {validation.touched.vat_percentage &&
                        validation.errors.vat_percentage ? (
                          <FormFeedback type="invalid">
                            {validation.errors.vat_percentage}
                          </FormFeedback>
                        ) : null}
                      </div>

                      {/* Amount Field */}
                      <div className="mb-3">
                        <Label htmlFor="amount-field" className="form-label">
                          Amount
                        </Label>
                        <Input
                          name="amount"
                          id="amount-field"
                          className="form-control"
                          placeholder="Enter Total Amount"
                          type="number"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.amount || ""}
                          invalid={
                            validation.touched.amount &&
                            validation.errors.amount
                              ? true
                              : false
                          }
                        />
                        {validation.touched.amount &&
                        validation.errors.amount ? (
                          <FormFeedback type="invalid">
                            {validation.errors.amount}
                          </FormFeedback>
                        ) : null}
                      </div>

                      {/* Cashed Field */}
                      <div className="mb-3">
                        <Label htmlFor="cashed-field" className="form-label">
                          Cashed
                        </Label>
                        <Input
                          name="cashed"
                          id="cashed-field"
                          className="form-control"
                          placeholder="Enter Cashed Amount"
                          type="number"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.cashed || ""}
                          invalid={
                            validation.touched.cashed &&
                            validation.errors.cashed
                              ? true
                              : false
                          }
                        />
                        {validation.touched.cashed &&
                        validation.errors.cashed ? (
                          <FormFeedback type="invalid">
                            {validation.errors.cashed}
                          </FormFeedback>
                        ) : null}
                      </div>
                      <div className="mb-3">
                        <Label htmlFor="unit-field" className="form-label">
                          Settlement Price
                        </Label>
                        <Input
                          name="settlement_price"
                          id="settlement_price-field"
                          type="select"
                          className="form-select"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.settlement_price || ""}
                          invalid={
                            validation.touched.settlement_price &&
                            validation.errors.settlement_price
                              ? true
                              : false
                          }
                        >
                          <option value="">Select Settelement Price</option>
                          {settlementPrice.map((set) => (
                            <option key={set.id} value={set.id}>
                              {set.id}
                            </option>
                          ))}
                        </Input>
                        {validation.touched.settlement_price &&
                        validation.errors.settlement_price ? (
                          <FormFeedback type="invalid">
                            {validation.errors.settlement_price}
                          </FormFeedback>
                        ) : null}
                      </div>
                      <div className="mb-3">
                        <Label
                          htmlFor="booking_date-field"
                          className="form-label"
                        >
                          Booking Date
                        </Label>
                        <Flatpickr
                          name="booking_date"
                          id="booking_date-field"
                          className={`form-control ${
                            validation.touched.booking_date &&
                            validation.errors.booking_date
                              ? "is-invalid"
                              : ""
                          }`}
                          placeholder="Select Booking Date required"
                          options={{
                            enableTime: true,
                            altInput: true,
                            altFormat: "d M, Y, G:i K",
                            dateFormat: "d M, Y, G:i K",
                          }}
                          onChange={(date) =>
                            validation.setFieldValue(
                              "booking_date",
                              moment(date[0]).toISOString()
                            )
                          }
                          value={validation.values.booking_date || ""}
                          onBlur={() =>
                            validation.setFieldTouched("booking_date", true)
                          }
                          invalid={
                            validation.touched.booking_date &&
                            validation.errors.booking_date
                              ? true
                              : false
                          }
                        />
                        {validation.touched.booking_date &&
                          validation.errors.booking_date && (
                            <FormFeedback type="invalid">
                              {validation.errors.booking_date}
                            </FormFeedback>
                          )}
                      </div>
                      <div className="mb-3">
                        <Label
                          htmlFor="cashed_date-field"
                          className="form-label"
                        >
                          Cashed Date
                        </Label>
                        <Flatpickr
                          name="cashed_date"
                          id="cashed_date-field"
                          className="form-control"
                          placeholder="Select Cashed Date"
                          options={{
                            enableTime: true,
                            altInput: true,
                            altFormat: "d M, Y, G:i K",
                            dateFormat: "d M, Y, G:i K",
                          }}
                          onChange={(date) =>
                            validation.setFieldValue(
                              "cashed_date",
                              moment(date[0]).toISOString()
                            )
                          }
                          value={validation.values.cashed_date || ""}
                        />
                      </div>
                    </ModalBody>

                    <div className="modal-footer">
                      <div className="hstack gap-2 justify-content-end">
                        <button
                          type="button"
                          className="btn btn-light"
                          onClick={() => {
                            setModal(false);
                          }}
                        >
                          Close
                        </button>

                        <button type="submit" className="btn btn-success">
                          {!!isEdit ? "Update" : "Add Payement"}
                        </button>
                      </div>
                    </div>
                  </Form>
                </Modal>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default EcommerceOrders;
